import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type OrderDbStatus =
  | "pending"
  | "payment_review"
  | "payment_rejected"
  | "paid"
  | "preparing"
  | "shipped"
  | "delivered"
  | "finished"
  | "cancelled";

export type OrderDisplayStatus =
  | "Pending"
  | "PaymentReview"
  | "PaymentRejected"
  | "Paid"
  | "Preparing"
  | "Shipped"
  | "Delivered"
  | "Finished"
  | "Cancelled";

export const mapStatus = (s: string): OrderDisplayStatus => {
  switch (s) {
    case "pending": return "Pending";
    case "payment_review": return "PaymentReview";
    case "payment_rejected": return "PaymentRejected";
    case "paid": return "Paid";
    case "preparing": return "Preparing";
    case "shipped": return "Shipped";
    case "delivered": return "Delivered";
    case "cancelled": return "Cancelled";
    case "finished": return "Finished";
    default: return "Finished";
  }
};

export interface UserOrder {
  id: string;
  dbId: string;
  cust: string;
  items: { pid: string; q: number }[];
  total: number;
  status: OrderDisplayStatus;
  date: string;
  tracking: string | null;
  ca: string | null;
}

/**
 * Owns the current user's orders: initial fetch + realtime updates for status
 * and tracking changes. Surfaces a toast hook so callers can show feedback
 * on transitions (status change, tracking added).
 */
export function useOrders(
  userId: string | null | undefined,
  lang: "en" | "es",
  toast: (msg: string) => void,
) {
  const [orders, setOrders] = useState<UserOrder[]>([]);
  const [selOrder, setSelOrder] = useState<UserOrder | null>(null);
  const [loading, setLoading] = useState(false);

  // Initial fetch
  useEffect(() => {
    if (!userId) {
      setOrders([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("*, order_items(product_id, quantity, price_per_item)")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) {
        setLoading(false);
        return;
      }
      const mapped: UserOrder[] = (data || []).map((o: any) => ({
        id: o.id.slice(0, 13).toUpperCase(),
        dbId: o.id,
        cust: lang === "es" ? "Tú" : "You",
        items: (o.order_items || []).map((oi: any) => ({ pid: oi.product_id, q: oi.quantity })),
        total: o.total_amount,
        status: mapStatus(o.status),
        date: (o.order_date || o.created_at || "").split("T")[0],
        tracking: o.tracking_number || null,
        ca: null,
      }));
      setOrders(mapped);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, lang]);

  // Realtime: status + tracking updates and INSERTs
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`orders:${userId}`, { config: { private: true } })
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "orders", filter: `user_id=eq.${userId}` },
        (payload: any) => {
          const u = payload.new;
          const oldStatus = payload.old?.status;
          const oldTracking = payload.old?.tracking_number;
          setOrders((prev) =>
            prev.map((o) =>
              o.dbId === u.id
                ? { ...o, status: mapStatus(u.status), tracking: u.tracking_number || o.tracking }
                : o,
            ),
          );
          if (oldStatus !== u.status) {
            toast(`📦 ${lang === "es" ? "Estado actualizado" : "Status updated"}: ${mapStatus(u.status)}`);
          } else if (oldTracking !== u.tracking_number && u.tracking_number) {
            toast(`🚚 ${lang === "es" ? "Tracking" : "Tracking"}: ${u.tracking_number}`);
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "orders", filter: `user_id=eq.${userId}` },
        (payload: any) => {
          const u = payload.new;
          setOrders((prev) =>
            prev.some((o) => o.dbId === u.id)
              ? prev
              : [
                  {
                    id: u.id.slice(0, 13).toUpperCase(),
                    dbId: u.id,
                    cust: lang === "es" ? "Tú" : "You",
                    items: [],
                    total: Number(u.total_amount || 0),
                    status: mapStatus(u.status),
                    date: (u.created_at || u.order_date || new Date().toISOString()).split("T")[0],
                    tracking: u.tracking_number || null,
                    ca: null,
                  },
                  ...prev,
                ],
          );
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, lang, toast]);

  // Used by checkout to optimistically prepend a new order
  const prepend = useCallback((o: UserOrder) => {
    setOrders((p) => (p.some((x) => x.dbId === o.dbId) ? p : [o, ...p]));
  }, []);

  return { orders, setOrders, selOrder, setSelOrder, loading, prepend };
}